import { useState } from "react";

import { dateFormat } from "common/dateFormat";
import { SortOrder } from "common/SortOrder";
import {
  getFileNameWithExtension,
  getFileNameWithoutExtension
} from "common/getFileName";
import { Button } from "components/button/Button";
import FolderTree from "components/folderTree/FolderTree";
import { Icon, SortIcon } from "components/icon/Icon";
import { Input } from "components/input/Input";
import Modal from "components/modal/Modal";
import { Tooltip } from "components/tooltip/Tooltip";
import { ArchiveItem } from "models/ArchiveItem";
import { filterYearEndFolders, isReportsFolder } from "views/documents/state/documentArchiveOptions";
import {
  SortDocumentFunction,
  SortByName,
  SortByDate,
  SortByCreatedBy
} from "./DocumentArchiveFileTableInternal";
import { useRootStore } from "store";
import ModalFooter from "components/modal/ModalFooter";

type DocumentArchiveFileTableWithOptionsProps = {
  files: ArchiveItem[];
  folder: ArchiveItem;
};

export function DocumentArchiveFileTableWithOptions({
  files,
  folder
}: DocumentArchiveFileTableWithOptionsProps) {
  const { archive, global: globalState } = useRootStore((state) => state);

  const {
    downloadArchiveFile,
    markAsDeleted,
    moveInArchive,
    updateFileWithName,
    archiveRoot
  } = archive;

  const [showRenameFileModal, setShowRenameFileModal] = useState(false);
  const [renameFileName, setRenameFileName] = useState("");
  const [showRemoveFileModal, setShowRemoveFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<ArchiveItem | undefined>(
    undefined
  );
  const [showMoveFileModal, setShowMoveFileModal] = useState(false);
  const [selectedMoveFolderId, setSelectedMoveFolderId] = useState("");

  const [sortFunction, setSorting] = useState<SortDocumentFunction>(
    () => SortByName
  );
  const [sortOrder, setSortOrder] = useState(SortOrder.Descending);

  const setSortingFn = (selectedSortFn: SortDocumentFunction) => {
    setSortOrder(
      sortFunction === selectedSortFn && sortOrder === SortOrder.Descending
        ? SortOrder.Ascending
        : SortOrder.Descending
    );
    setSorting(() => selectedSortFn);
  };

  const onFileNameChanged = async (id: string, name: string) => {
    updateFileWithName(name, id, globalState.workingCustomerId);
    setRenameFileName("");
    setShowRenameFileModal(false);
  };

  const handleMoveInArchive = (
    pathToMoveTo: string,
    itemToMove: ArchiveItem,
    name: string
  ) => {
    moveInArchive(
      itemToMove,
      globalState.workingCustomerId,
      pathToMoveTo,
      name
    );
  };

  const removeFileConfirm = (file: ArchiveItem) => {
    markAsDeleted(file.id, globalState.workingCustomerId);
    setShowRemoveFileModal(false);
  };

  const openRemoveFileModal = (file: ArchiveItem) => {
    setShowRemoveFileModal(true);
    setSelectedFile(file);
  };

  const openRenameFileModal = (file: ArchiveItem) => {
    setShowRenameFileModal(true);
    setSelectedFile(file);
  };

  const openMoveFileModal = (file: ArchiveItem) => {
    setShowMoveFileModal(true);
    setSelectedFile(file);
    setSelectedMoveFolderId(folder.id || "root");
  };

  const selectedMoveFolder = archive.archiveRoot.find(
    (r) => r.id === selectedMoveFolderId
  );

  const canMoveToFolder = selectedMoveFolder && selectedMoveFolder.writable;

  const handleOnConfirmMoveFile = () => {
    setShowMoveFileModal(false);
    selectedMoveFolder &&
      selectedFile &&
      handleMoveInArchive(
        selectedMoveFolder.path,
        selectedFile,
        selectedFile.name
      );
  };

  return (
    <>
      {showMoveFileModal && (
        <Modal
          onDismiss={() => setShowMoveFileModal(false)}
          header={<h1 className="mt-0">Flytta fil till mapp</h1>}
          modalTitle="move file"
          footer={
            <>
              {!canMoveToFolder && (
                <p className="my-4">
                  <b>Välj en undermapp att flytta till</b>
                </p>
              )}

              {selectedMoveFolder && selectedMoveFolder.writable && (
                <p className="my-4">
                  Flytta filen <b>{selectedFile && selectedFile.name}</b> till{" "}
                  <b>{selectedMoveFolder.name}</b>?
                </p>
              )}
              <ModalFooter
                justifyActions="end"
                actionsClassName="mt-4"
                disableConfirm={!selectedMoveFolder || !canMoveToFolder}
                confirmText="Bekräfta"
                onConfirm={handleOnConfirmMoveFile}
                onCancel={() => setShowMoveFileModal(false)}
              />
            </>
          }
        >
          <hr />
          <FolderTree
            classNamesFolder="py-2"
            selectedFolderId={selectedMoveFolderId}
            folders={archive.archiveRoot.filter((item) =>
              filterYearEndFolders(item, archiveRoot)
            )}
            onFolderClick={(clickedFolder) =>
              setSelectedMoveFolderId(clickedFolder.id)
            }
          />
          <hr />
        </Modal>
      )}

      {showRemoveFileModal && selectedFile && (
        <Modal
          header={<h1 className="m-0">Ta bort fil permanent</h1>}
          onDismiss={() => setShowRemoveFileModal(false)}
          modalTitle="remove file"
        >
          <div>
            <p>Vill du ta bort filen {selectedFile.name} permanent?</p>
            <div className="d-flex justify-content-end">
              <Button
                className="mr-3"
                onClick={() => removeFileConfirm(selectedFile)}
              >
                Bekräfta
              </Button>
              <Button
                variant="outline"
                color="light"
                onClick={() => setShowRemoveFileModal(false)}
              >
                Avbryt
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showRenameFileModal && selectedFile && (
        <Modal
          header={<h1 className="m-0">Byt namn på fil</h1>}
          onDismiss={() => setShowRenameFileModal(false)}
          modalTitle="rename file"
        >
          <div>
            <p>Ange ett nytt filnamn</p>

            <Input
              defaultValue={getFileNameWithoutExtension(selectedFile.name)}
              onChange={(e) => setRenameFileName(e.target.value)}
            />

            <div className="mt-4 d-flex justify-content-end">
              <Button
                className="mr-3"
                onClick={() =>
                  onFileNameChanged(
                    selectedFile.id,
                    getFileNameWithExtension(renameFileName, selectedFile.name)
                  )
                }
              >
                Bekräfta
              </Button>

              <Button
                variant="outline"
                color="light"
                onClick={() => setShowRenameFileModal(false)}
              >
                Avbryt
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <table data-testid="document-archive-table-with-options">
        <thead>
          <tr>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByName)}
            >
              <div className="d-flex">
                Namn
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByName ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByDate)}
            >
              <div className="d-flex">
                Uppladdat den
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByDate ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByCreatedBy)}
            >
              <div className="d-flex">
                Uppladdat av
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByCreatedBy ? sortOrder : ""}
                />
              </div>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {files
            .sort((a, b) => {
              return (
                sortFunction(a, b) *
                (sortOrder === SortOrder.Descending ? 1 : -1)
              );
            })
            .map((f) => (
              <tr key={f.id}>
                <td className="break-word">{f.name}</td>
                <td>{dateFormat(new Date(f.createdAt))}</td>
                <td>{f.createdBy}</td>
                <td>
                  <div className="d-flex fg-2">
                    <Tooltip
                      className="mr-3"
                      content={<p className="m-0">Ladda ned filen</p>}
                    >
                      <Icon
                        name="download"
                        onClick={() => downloadArchiveFile(f)}
                      />
                    </Tooltip>
                    {!isReportsFolder(folder.id) && (
                      <>
                        <Tooltip
                          className="mr-3"
                          content={<p className="m-0">Byt namn på filen</p>}
                        >
                          <Icon
                            className="cursor-pointer"
                            name="pencil"
                            onClick={() => openRenameFileModal(f)}
                          />
                        </Tooltip>

                        <Tooltip
                          className="mr-3"
                          content={<p className="m-0">Ta bort filen</p>}
                        >
                          <Icon
                            name="trash-o"
                            onClick={() => openRemoveFileModal(f)}
                          />
                        </Tooltip>

                        <Tooltip
                          content={
                            <p className="m-0">
                              Flytta filen till en annan mapp
                            </p>
                          }
                        >
                          <Icon
                            name="folder-o"
                            onClick={() => openMoveFileModal(f)}
                          />
                        </Tooltip>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {files.length === 0 && <p>Här var det tomt!</p>}
    </>
  );
}
